import { createCachedSelector, LruObjectCache } from 're-reselect';
import { createSelector } from 'reselect';

import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';

import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import { type SECURITIES_REDUCER_KEY, securitiesSelectors, securitiesUtils } from '../securities';
import type { SecurityProps } from '../securities/securitiesTypes';
import { INVESTMENT_SYMBOLS_KEY, type InvestmentSymbolsStore, mkInvestmentSymbol } from './investmentSymbolsTypes';

type State = {
  [INVESTMENT_SYMBOLS_KEY]: InvestmentSymbolsStore;
  [SECURITIES_REDUCER_KEY]: QcsSyncResourceStore<SecurityProps>;
};
type Filter = string | null | undefined;

export const getResourceStore = (state: State) => state[INVESTMENT_SYMBOLS_KEY];
export const getLoadPending = (state: State) => getResourceStore(state).loadPending;
export const getIsLoading = (state: State) => getResourceStore(state).isLoading;
export const getResourcesById = (state: State) => getResourceStore(state).resourcesById;

export const getInvestmentSymbolsUnion = createSelector(
  getResourcesById,
  securitiesSelectors.getResourcesById,
  (symbols, securities) =>
    symbols.withMutations((map) => {
      securities.forEach((security) => {
        const symbol = security.symbol;
        if (symbol == null) {
          return;
        }

        const symbolMatch = map.get(symbol);
        if (symbolMatch) {
          map.set(symbol, symbolMatch.set('security', security));
        } else {
          const id = securitiesUtils.securityKey(security);
          map.set(
            id,
            mkInvestmentSymbol({
              id,
              security,
              name: security.name ?? '',
              symbol,
            }),
          );
        }
      });
    }),
);

export const getInvestmentSymbolsFiltered = createCachedSelector(
  getInvestmentSymbolsUnion,
  (_state: State, filter: Filter) => filter?.toUpperCase(),
  (investmentSymbols, filter) =>
    investmentSymbols
      .filter(
        (investmentSymbol) =>
          filter &&
          (investmentSymbol.symbol?.toUpperCase().includes(filter) ||
            investmentSymbol.name.toUpperCase().includes(filter)),
      )
      .toList()
      .sort(
        (investmentSymbol1, investmentSymbol2) =>
          (investmentSymbol1.symbol?.toUpperCase().startsWith(filter!) ? 0 : 1) -
            (investmentSymbol2.symbol?.toUpperCase().startsWith(filter!) ? 0 : 1) ||
          (investmentSymbol1.symbol?.toUpperCase().includes(filter!) ? 0 : 1) -
            (investmentSymbol2.symbol?.toUpperCase().includes(filter!) ? 0 : 1) ||
          investmentSymbol1.symbol.localeCompare(investmentSymbol2.symbol),
      ),
)({
  keySelector: (_state: State, filter: Filter) => filter ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(getInvestmentSymbolsFiltered);
